import { type ReactNode } from 'react';
import * as React from 'react';
import { rgba } from 'polished';
import styled, { css } from 'styled-components';

import { ArrowTopRightIcon } from '../../../icons/Icons';
import { Text } from '../../design-system';
import { LinkWithQuery } from '../LinkWithQuery';

export type TextAlignment = 'left' | 'center' | 'right';

interface WrapperProps {
  $align?: TextAlignment;
  $description?: string;
  $fill?: boolean;
  $hoverActive?: boolean;
}

const wrapperStyles = css<WrapperProps>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  height: ${({ $description }) => ($description ? 'auto' : '40px')};
  color: ${({ theme }) => theme.fontColor.LOUD};
  padding: ${({ $description }) => ($description ? ' 10px 15px' : ' 0 15px')};
  border-color: transparent;
  cursor: pointer;

  &:hover {
    background-color: transparent;
  }

  &::after {
    content: '';
    display: block;
    box-sizing: border-box;
    position: absolute;
    top: 0;
    right: 0;
    left: 0;
    bottom: 0;
    width: 100%;
    height: 100%;
    border: 1px solid transparent;
    background: linear-gradient(
        to right,
        ${({ theme }) => theme.backgroundColor},
        ${({ theme }) => theme.backgroundColor}
      ),
      linear-gradient(
        45deg,
        ${({ theme }) => rgba(theme.highlightColor, 0.5)},
        ${({ theme }) => rgba(theme.colors.LIGHT_PURPLE, 0.5)}
      );
    background-clip: padding-box, border-box;
    background-origin: padding-box, border-box;
    z-index: 0;
    opacity: 0;
    transition: opacity ${({ theme }) => theme.animation.DURATION}
      ${({ theme }) => theme.animation.EASING};
  }

  &:hover::after {
    opacity: 0.4;
  }

  &:active::after,
  &.active::after {
    opacity: 1;
  }

  ${({ $hoverActive }) =>
    $hoverActive &&
    css`
      &::after {
        opacity: 1;
      }
    `}

  > svg {
    position: relative;
    flex: none;
    z-index: 1;
  }

  ${({ $align }) =>
    $align === 'left' &&
    css`
      justify-content: flex-start;
    `}

  ${({ $fill }) =>
    $fill &&
    css`
      flex: 1 1 auto;
      width: 100%;
    `}
`;

const ExternalIcon = styled.div`
  display: flex;
  margin-left: auto;
  transition: opacity ${({ theme }) => theme.animation.DURATION}
    ${({ theme }) => theme.animation.EASING};
`;

const LinkWrapper = styled(LinkWithQuery)<WrapperProps>`
  ${wrapperStyles};

  ${ExternalIcon} {
    opacity: 0;
  }

  &:hover {
    ${ExternalIcon} {
      opacity: 1;
    }
  }
`;

const DivWrapper = styled.div<WrapperProps>`
  ${wrapperStyles};
`;

const Inner = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  position: relative;
  z-index: 1;
  width: 100%;
`;

const Info = styled.div`
  display: flex;
  align-items: center;

  svg {
    color: ${({ theme }) => theme.iconColor.DEFAULT};
  }
`;

const Label = styled.div`
  flex: none;
  margin-left: 8px;
  display: flex;
  align-items: center;
  gap: 7px;
`;

const Description = styled(Text)`
  margin: 0;
  padding-left: 24px;
`;

interface NavItemProps {
  align?: TextAlignment;
  description?: string;
  fill?: boolean;
  icon?: ReactNode;
  isExternal?: boolean;
  isHoverActive?: boolean;
  onMouseEnter?: () => void;
  onMouseLeave?: () => void;
  partiallyActive?: boolean;
  text: ReactNode;
  to?: string;
}

export const NavItem: React.ComponentType<NavItemProps> = React.memo(
  ({
    align = 'center',
    description,
    fill = false,
    isHoverActive = false,
    isExternal = false,
    to,
    onMouseEnter,
    onMouseLeave,
    text,
    icon,
    partiallyActive = false
  }) => {
    const content = (
      <Inner>
        <Info>
          {icon && icon}
          <Label>{text}</Label>
          {isExternal && (
            <ExternalIcon>
              <ArrowTopRightIcon />
            </ExternalIcon>
          )}
        </Info>
        {description && <Description size="small">{description}</Description>}
      </Inner>
    );

    if (!to) {
      return (
        <DivWrapper
          $description={description}
          $hoverActive={isHoverActive}
          className="item"
          onMouseEnter={onMouseEnter}
          onMouseLeave={onMouseLeave}>
          {content}
        </DivWrapper>
      );
    }

    return (
      <LinkWrapper
        $align={align}
        $description={description}
        $fill={fill}
        $hoverActive={isHoverActive}
        activeClassName="active"
        className="item"
        isExternal={isExternal}
        partiallyActive={partiallyActive}
        to={to}>
        {content}
      </LinkWrapper>
    );
  }
);
